import React from 'react';
import moment from 'moment';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  descend,
  find,
  flatten,
  isEmpty,
  pickBy,
  pluck,
  prop,
  propEq,
  sortWith,
  uniqBy,
} from 'ramda';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Graphic from 'components/Graphic/Graphic';
import { FilterType, SearchFilter } from 'components/SearchFilter/SearchFilter';
import { motion } from 'framer-motion';
import { patientsQuery } from 'components/Layout/Layout';
import Icon from '../../components/Icon/Icon';
import Patient from '../../components/Patient/Patient';
import {
  getPatientsQuestionnaires,
  sortPatientsVisitsByMonthYear,
  useAppContext,
} from '../../context/appContext';
import {
  VISIT_STATUS,
  VISIT_TYPE_CONSTANTS,
  visitIconsAndColors,
  visitTypes,
} from '../../context/visitContext';
import { QuestionAnswers, Patient as PatientType } from '../../types/types';
import {
  capitalize,
  hasOpenedFollowUp,
  hasOpenedLabReport,
  isRole,
  moveFilteredItemsToTop,
  sessionTStorage,
  TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY,
  US_STATES,
} from '../../utils/utils';
import { paths } from '../../routes';

const patientsQuestionnairesQuery = (patients: PatientType[], userId?: string) => ({
  queryKey: ['patients-questionnaires', patients, userId], // we want to refresh when patient data changes
  queryFn: () => getPatientsQuestionnaires(patients, userId),
});

const PATIENTS_LIMIT = 5;

function Home() {
  const [{ user, patients, unReadMessages: unReadAnsweredQnAs }, dispatch] = useAppContext();
  const [patientsLimit, setPatientsLimit] = React.useState(PATIENTS_LIMIT);
  const [showFilterView, setShowFilterView] = React.useState(false);
  const [filteredPatients, setFilteredPatients] = React.useState<PatientType[]>();
  const [expandedSearch, setExpandedSearch] = React.useState<Record<string, string>>();
  const [searchTypingTimer, setSearchTypingTimer] = React.useState<NodeJS.Timeout | undefined>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const doneSearchTypingInterval = 500;

  const { data, isLoading, isFetching } = useQuery({
    ...patientsQuestionnairesQuery(patients, user?.id),
    enabled: !!(patients.length && user?.id),
    staleTime: 1000 * 50,
  });

  const advancedSearch = useQuery({
    ...patientsQuery(user, expandedSearch),
    queryKey: ['filtered-patients', expandedSearch],
    enabled: !!expandedSearch?.q,
  });

  const queryParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [patientsWithVisits, visits] = React.useMemo(() => {
    const fndPatientsVisits = data || [patients, sortPatientsVisitsByMonthYear(patients)] || [];

    let fndPendingVisits = flatten(pluck('questionnaires', fndPatientsVisits[0])).filter(
      fndQuestion => {
        if (!isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')) {
          const isPending = fndQuestion.status === VISIT_STATUS.UNANSWERED;
          return (
            isPending ||
            (fndQuestion.followUps && !hasOpenedFollowUp(fndQuestion.followUps)) ||
            (fndQuestion.labTests && !hasOpenedLabReport(fndQuestion.labTests))
          );
        }
        return fndQuestion.status === VISIT_STATUS.COMPLETE;
      },
    );

    fndPendingVisits = sortWith([descend(prop('createdAt'))], fndPendingVisits);

    fndPendingVisits = moveFilteredItemsToTop({
      array: fndPendingVisits,
      findFunction: fndQuestion =>
        Boolean(fndQuestion.labTests && !hasOpenedLabReport(fndQuestion.labTests)),
    });

    return [fndPatientsVisits[0], fndPendingVisits];
  }, [data, patients, user?.roles]);

  React.useEffect(() => {
    // Clear the timer if the component is unmounted
    return () => clearTimeout(searchTypingTimer);
  }, [searchTypingTimer]);

  React.useEffect(() => {
    setFilteredPatients(advancedSearch.data);
  }, [advancedSearch.data]);

  const handlePatientFiltering = (value: FilterType) => {
    const searchValues = pickBy(search => Boolean(search && !isEmpty(search)), value) as FilterType;

    if (isEmpty(searchValues)) {
      setFilteredPatients(undefined);
      return;
    }

    const fPatients = patients.filter(fndPatient => {
      let isMatch = true;

      if (searchValues.name) {
        isMatch =
          isMatch &&
          (`${fndPatient.firstName} ${fndPatient.lastName}`
            .toLowerCase()
            .includes(searchValues.name.toLowerCase()) ||
            `${fndPatient.lastName} ${fndPatient.firstName}`
              .toLowerCase()
              .includes(searchValues.name.toLowerCase()));
      }

      if (searchValues.state) {
        isMatch = isMatch && fndPatient.state === searchValues.state;
      }

      if (searchValues.gender) {
        isMatch = isMatch && fndPatient.patientDetails.gender === searchValues.gender;
      }

      if (searchValues.provider) {
        isMatch =
          isMatch &&
          (`${fndPatient?.assignedProvider?.firstName} ${fndPatient?.assignedProvider?.lastName}`
            .toLowerCase()
            .includes(searchValues.provider.toLowerCase()) ||
            `${fndPatient?.assignedProvider?.lastName} ${fndPatient?.assignedProvider?.firstName}`
              .toLowerCase()
              .includes(searchValues.provider.toLowerCase()));
      }

      return isMatch;
    });

    if (fPatients && fPatients.length > 0) {
      setFilteredPatients(fPatients);
      return;
    }

    setFilteredPatients([]);

    clearTimeout(doneSearchTypingInterval);

    const newTimer = setTimeout(() => {
      setExpandedSearch({
        q: searchValues?.name || '',
        // q: Object.values(searchValues)
        //   .filter(sValue => sValue)
        //   .join('|'),
        state: searchValues?.state,
        gender: searchValues?.gender,
      }); // TODO: take a look at this filter again
    }, doneSearchTypingInterval);

    setSearchTypingTimer(newTimer);
  };

  const handlePatientClicked = (patient: PatientType) => {
    if (!find(propEq('id', patient.id))(patientsWithVisits)) {
      sessionTStorage.set(TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY, JSON.stringify(patient));
    }

    dispatch({
      type: 'SET_ASSIGNED_PATIENTS',
      payload: uniqBy(prop('id'), [...patientsWithVisits, patient]),
    });
    navigate(`${paths.PATIENT_URL_PATH.replace(':patientId', patient.id)}`);
  };

  const handleVisitClicked = (visit: QuestionAnswers) => {
    dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: patientsWithVisits });

    const url = new URL(
      `${window.location.origin}${paths.VISIT_RECORDS_URL_PATH.replace(':visitId', visit.id)}`,
    );

    url.searchParams.append('type', visit.type.toLocaleLowerCase());

    url.searchParams.append('patient', visit.patientUserId);

    if (hasUnReadAnsweredQnAs(visit) || hasANewFollowUp(visit) || hasANewLabReport(visit)) {
      url.pathname = hasANewFollowUp(visit)
        ? `${url.pathname}/treatment-plan`
        : hasUnReadAnsweredQnAs(visit)
        ? `${url.pathname}/chat`
        : `${url.pathname}/labs`;

      url.hash = hasANewFollowUp(visit) ? '#follow-ups' : '';

      if (hasUnReadAnsweredQnAs(visit)) {
        const QnAsId = unReadAnsweredQnAs?.find(
          fndUnReadQnAs => fndUnReadQnAs?.visitId === visit.id,
        )?.QnAs?.id;

        if (QnAsId) {
          url.searchParams.append('QnAsId', QnAsId);
        }
      }

      const { queryKey } = patientsQuestionnairesQuery(patients, user?.id);
      queryClient.invalidateQueries({ queryKey });
    }

    navigate(url.toString().replace(window.location.origin, ''));
  };

  const getVisitTitle = (type: string) => {
    const findVisit = visitTypes.find(fndVisit => fndVisit.type === type.toLocaleUpperCase());
    if (!findVisit) return type;
    return [VISIT_TYPE_CONSTANTS.SICK_VISITS, VISIT_TYPE_CONSTANTS.UTI_VISITS].includes(
      findVisit.type,
    )
      ? `${findVisit.name} Visit`
      : findVisit.name;
  };

  const getVisitPatientName = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return `${findPatient?.firstName} ${findPatient?.lastName}`;
  };

  const getVisitPatientSignedAvatar = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return findPatient?.profilePicture || '';
  };

  const getVisitPatientDOBAndGender = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return findPatient ? getPatientDOBAndGender(findPatient) : '-';
  };

  const getVisitPatientState = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return US_STATES.find(state => state.value === findPatient?.state)?.label || '-';
  };

  const getPatientDOBAndGender = (patient: PatientType) => {
    const dob =
      moment().diff(moment(patient.patientDetails?.dateOfBirth).format('YYYY-MM-DD'), 'years') || 0;

    if (dob && patient.patientDetails?.gender) {
      return `${dob}, ${capitalize(patient.patientDetails?.gender)}`;
    }

    if (dob) {
      return `${dob} years`;
    }

    if (patient.patientDetails?.gender) {
      return capitalize(patient.patientDetails?.gender);
    }

    return '-';
  };

  const hasANewLabReport = (visit: QuestionAnswers) => {
    return visit.labTests && !hasOpenedLabReport(visit.labTests);
  };

  const hasANewFollowUp = (visit: QuestionAnswers) => {
    return visit.followUps && !hasOpenedFollowUp(visit.followUps);
  };

  const hasUnReadAnsweredQnAs = (visit: QuestionAnswers) => {
    return Boolean(unReadAnsweredQnAs?.find(fndUnReadQnAs => fndUnReadQnAs?.visitId === visit.id));
  };

  if (queryParams.get('photon-redirect')) {
    return <Navigate to={queryParams.get('photon-redirect') || paths.HOME_URL_PATH} replace />;
  }

  return (
    <div className="relative h-full flex flex-col justify-center items-center pt-4">
      <div className="text-center tablet:text-center">
        <h1 className="font-semibold text-[25px] tablet:text-[30px] text-river-black">
          Welcome, {user?.firstName || 'Doc'}
        </h1>
      </div>

      <div
        className="relative pt-10 pb-5 w-full space-y-8 laptop:pt-8 laptop:pb-8 laptop:w-3/4 mx-auto laptop:space-y-10 
          px-4 tablet:px-10 desktop:px-0"
      >
        <div className="flex flex-col">
          <div className="flex flex-row justify-between mb-5">
            <div className="text-neutral-500 text-xs uppercase font-medium">
              {isLoading && isFetching ? (
                <Skeleton width={125} height={24} className="block rounded-full" duration={1.4} />
              ) : !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? (
                'PENDING VISITS'
              ) : (
                'VISITS'
              )}
            </div>

            {isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? (
              <button
                onClick={() =>
                  navigate(
                    `${paths.VISITS_URL_PATH}?status=${
                      !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? 'pending' : 'complete'
                    }`,
                  )
                }
                type="button"
                disabled={visits.length === 0}
                className="text-river-blue inline-flex items-center text-xs font-medium disabled:opacity-50"
              >
                {isLoading && isFetching ? (
                  <Skeleton width={90} height={24} className="block rounded-full" duration={1.4} />
                ) : (
                  'VIEW ALL'
                )}
              </button>
            ) : null}
          </div>

          <div className="space-y-5">
            {isLoading && isFetching ? (
              <div className="w-full flex flex-col space-y-4">
                {new Array(4).fill(null).map((_, index) => (
                  <Skeleton
                    key={index}
                    width="100%"
                    height={53}
                    className="block rounded-full"
                    duration={1.4}
                  />
                ))}
              </div>
            ) : visits.length === 0 ? (
              <div className="w-full flex flex-col justify-center items-center">
                <Graphic id="graphicNoPendingVisit" className="h-[120px] tablet:h-[130px]" />
                <div className="text-center mt-3">
                  <p className="font-medium">You’re all caught up on your visits</p>
                  <p className="text-neutral-500 text-xs">See all pending visits in one place</p>
                </div>
              </div>
            ) : (
              visits
                .slice(0, isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? 5 : undefined)
                .map(visit => (
                  <div
                    key={visit.id}
                    role="presentation"
                    onClick={() => handleVisitClicked(visit)}
                    className={`flex cursor-pointer relative justify-between laptop:grid laptop:grid-cols-7 py-1 
                      items-center rounded-full hover:bg-[#F6FAFF] ${
                        hasANewLabReport(visit) ? 'font-semibold' : ''
                      }`}
                  >
                    {(hasUnReadAnsweredQnAs(visit) ||
                      hasANewFollowUp(visit) ||
                      hasANewLabReport(visit)) && (
                      <span className="w-[8px] h-[8px] bg-river-deep-orange rounded-full shrink-0 absolute -left-4" />
                    )}
                    <div className="laptop:col-span-2 flex items-center space-x-6 w-full ">
                      <div
                        style={{
                          backgroundColor: hasANewLabReport(visit)
                            ? '#F6F2FC'
                            : visitIconsAndColors[visit.type.toLocaleLowerCase()]?.bgColor ||
                              '#FFE0E3',
                        }}
                        className="w-[50px] h-[50px] rounded-full flex items-center justify-center shrink-0"
                      >
                        <Icon
                          className={`icon-visit ${
                            hasANewLabReport(visit)
                              ? 'icon-lab-result'
                              : `icon-${visit.type.toLocaleLowerCase()}-visit`
                          }`}
                          id={
                            hasANewLabReport(visit)
                              ? 'iconLabResult'
                              : visitIconsAndColors[visit.type.toLocaleLowerCase()]?.icon || ''
                          }
                          style={{
                            color: hasANewLabReport(visit)
                              ? '#6E2FF3'
                              : visitIconsAndColors[visit.type.toLocaleLowerCase()]?.color || '',
                          }}
                        />
                      </div>
                      <div className="w-full flex flex-col">
                        <div>
                          {visit.labTests && !hasOpenedLabReport(visit.labTests)
                            ? 'Lab Results'
                            : getVisitTitle(visit.type)}
                        </div>
                        <div className="flex flex-flex justify-between w-full laptop:hidden items-baseline">
                          <div className="text-sm inline-flex">
                            {getVisitPatientName(visit.patientUserId)}
                          </div>

                          <div className="items-center flex">
                            <Icon
                              id="iconArrowForward"
                              className="w-4 text-[#737373] group-hover:text-river-black"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="w-full laptop:col-span-5 hidden laptop:flex">
                      <Patient
                        key={visit.patientUserId}
                        avatar={getVisitPatientSignedAvatar(visit.patientUserId)}
                        avatarBgColor="#DFE5E7"
                        name={getVisitPatientName(visit.patientUserId)}
                        about={getVisitPatientDOBAndGender(visit.patientUserId)}
                        state={getVisitPatientState(visit.patientUserId)}
                        onPatientClick={() => false}
                        isLoadingPatient={false}
                        showArrowNext
                      />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        <hr className="block border-b-[1px] border-t-0 border-[#e9ecef]" />

        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center mb-5">
            <div className="text-neutral-500 text-xs uppercase font-medium shrink-0">
              PATIENT PANEL
            </div>

            <div className="relative w-full tablet:w-[80%] flex flex-row justify-end">
              <motion.div
                layout
                initial={false}
                animate={showFilterView ? 'open' : 'closed'}
                variants={{
                  open: { opacity: 1, display: 'block', position: 'absolute' },
                  closed: { opacity: 0, display: 'none' },
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="w-full"
              >
                <SearchFilter
                  fields={
                    user && !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')
                      ? ['name', 'gender', 'state']
                      : ['name', 'state', 'provider']
                  }
                  onSearchViewClose={() => setShowFilterView(!showFilterView)}
                  onFilter={handlePatientFiltering}
                />
              </motion.div>

              <motion.button
                initial={false}
                animate={showFilterView ? 'open' : 'closed'}
                onClick={() => setShowFilterView(true)}
                variants={{
                  open: { opacity: 0, zIndex: -1 },
                  closed: { opacity: 1, zIndex: 10 },
                }}
                transition={{ duration: 0.5, ease: 'easeIn' }}
                className="bg-white border text-xs rounded-full py-[5px] px-5 inline-flex items-center 
                    flex-shrink-0 hover:bg-river-black hover:border-bg-river-black hover:text-white group"
              >
                <Icon
                  id="iconFilter"
                  className="w-4 text-river-black mr-2 group-hover:text-white"
                />
                Filters
              </motion.button>
            </div>
          </div>

          <div className="space-y-5">
            {isEmpty(patientsWithVisits) && patientsWithVisits.length === 0 ? (
              <div className="w-full flex flex-col justify-center items-center">
                <Graphic id="graphicNoPatients" className="h-[160px]" />
                <div className="text-center mt-2">
                  <p className="font-medium">All clear! No patients assigned.</p>
                  <p className="text-[#7A7A7A] text-xs">View assignments in one place.</p>
                </div>
              </div>
            ) : isEmpty(filteredPatients) ? (
              <div className="w-full flex flex-col justify-center items-center mt-10">
                {advancedSearch.isFetching ? (
                  <>
                    <Icon id="loader" className="w-8 h-8 self-center" />

                    <div className="text-center self-center mt-2">
                      <p className="font-medium">Please wait...</p>
                      <p className="text-[#7A7A7A] text-xs">
                        Performing a wider search for more results.
                      </p>
                    </div>
                  </>
                ) : advancedSearch.status === 'error' ? (
                  <div className="text-center self-center mt-2">
                    <p className="font-medium">No results found.</p>
                    <p className="text-[#7A7A7A] text-xs">Search for more results failed.</p>
                  </div>
                ) : null}
              </div>
            ) : (
              (filteredPatients || patientsWithVisits)
                .slice(0, patientsLimit)
                .map((patient: PatientType) => (
                  <Patient
                    key={patient.id}
                    avatar={patient.profilePicture}
                    avatarBgColor="#DFE5E7"
                    name={`${patient.firstName} ${patient.lastName}`}
                    about={getPatientDOBAndGender(patient)}
                    state={US_STATES.find(state => state.value === patient.state)?.label || '-'}
                    onPatientClick={() => handlePatientClicked(patient)}
                    isLoadingPatient={false}
                  />
                ))
            )}
          </div>

          {patientsWithVisits.length > PATIENTS_LIMIT &&
            patientsLimit === PATIENTS_LIMIT &&
            !filteredPatients && (
              <div className="text-center pb-8 text-river-blue mt-4">
                <button
                  onClick={() => setPatientsLimit(patientsWithVisits.length)}
                  type="button"
                  className="p-2 !font-medium"
                >
                  Show more patients...
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default Home;
